import moment from "moment-timezone";

/**
 * Reading and writing DSA schedule event times.
 *
 * A timezone-agnostic event stores a fixed wall clock: `startTime` / `endTime` carry the time of day
 * with no timezone attached, and `startDate` / `endDate` carry the same wall clock pinned to UTC. It
 * therefore renders identically in every browser and plays at that wall clock local to whichever
 * screen it runs on.
 *
 * A legacy event stores an absolute instant derived from the creating user's browser timezone, so it
 * still renders in the viewer's timezone. That behavior is deliberately left alone.
 *
 * Every read and write of an event's time goes through this module — no component should do date math
 * on `startDate` directly. See the design spec in the shared repo:
 * docs/superpowers/specs/2026-07-28-dsa-schedule-timezone-agnostic-design.md
 */

/** The time-carrying subset of DsaScheduleEventDto, which a DTO satisfies structurally. */
export type ScheduleEventTimes = {
  startDate?: number;
  endDate?: number;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  timezoneAgnostic?: boolean;
};

/**
 * Encodes a wall clock as the millis we store for a timezone-agnostic event. Pinning to UTC is what
 * makes the value independent of the browser that wrote it.
 *
 * Keep this the only writer: the backend reads the calendar date from these millis and the time of
 * day from `startTime` / `endTime`, so the two must always agree.
 */
export function toStoredMillis(dateStr: string, timeStr: string): number {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes, seconds = 0] = timeStr.split(":").map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes, seconds);
}

/**
 * The wall clock to show for an event. For an agnostic event the moments are held in UTC and never
 * converted, so formatting and diffing them is stable in every timezone.
 */
export function eventWallClock(ev: ScheduleEventTimes): { start: moment.Moment; end: moment.Moment } {
  return ev.timezoneAgnostic
    ? { start: moment.utc(ev.startDate), end: moment.utc(ev.endDate) }
    : { start: moment(ev.startDate), end: moment(ev.endDate) };
}

/**
 * Master switch for storing schedule times as a fixed wall clock. Flip to true to turn the feature on.
 *
 * While false, nothing ever writes the flag, so every event is stored the old creator-anchored way and
 * the whole feature is inert: the backend never takes its timezone-agnostic branch, `eventWallClock`
 * always renders in the viewer's timezone, and `nowInEventFrame` leaves "now" alone.
 */
export const TIMEZONE_AGNOSTIC_SCHEDULES = false;

/**
 * Whether the event being saved should be stored as a fixed wall clock.
 *
 * A new event follows [TIMEZONE_AGNOSTIC_SCHEDULES]. An existing event keeps whatever it already was,
 * whichever way the switch is set: editing must not silently re-anchor when an event fires on the
 * device, and an event created while the switch was on keeps working after it is turned off.
 */
export function isTimezoneAgnostic(existing?: ScheduleEventTimes): boolean {
  return existing ? existing.timezoneAgnostic === true : TIMEZONE_AGNOSTIC_SCHEDULES;
}

/**
 * The millis of a wall-clock moment with its timezone discarded, in the same UTC-pinned encoding
 * [toStoredMillis] produces.
 *
 * Use it to put a legacy and an agnostic event into one comparable frame — the frame the user reads
 * off the screen — for things like overlap detection.
 */
export function wallClockMillis(m: moment.Moment): number {
  return Date.UTC(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
}

/**
 * Re-expresses "now" in the frame the event's stored millis live in, so the two can be compared.
 *
 * For an agnostic event that means the wall clock now showing on the screen the event plays on —
 * the screen's timezone decides playback, not the viewer's. Falls back to the viewer's timezone when
 * the screen has none set.
 */
export function nowInEventFrame(ev: ScheduleEventTimes, nowMs: number, screenTimezone?: string): number {
  if (!ev.timezoneAgnostic) return nowMs;

  const local = screenTimezone ? moment.tz(nowMs, screenTimezone) : moment(nowMs);
  return Date.UTC(local.year(), local.month(), local.date(), local.hour(), local.minute(), local.second());
}
